// utils/routeTimeUtils.js
import moment from 'moment';

/**
 * Extract time-related data from a route
 * @param {Object} route - Route object
 * @returns {Object} Time data summary
 */
export const extractRouteTimeData = (route) => {
  if (!route) {
    return {
      totalDriveTime: 0,
      totalServiceTime: 0,
      firstArrival: null,
      lastDeparture: null,
      hasOptimizedTimes: false
    };
  }

  const firstStop = route.stops?.[0];
  const lastStop = route.stops?.[route.stops.length - 1];

  return {
    totalDriveTime: route.totalDriveTime || 0,
    totalServiceTime: route.totalServiceTime || 0,
    firstArrival: firstStop?.estimatedArrivalTime || null,
    lastDeparture: lastStop?.estimatedDepartureTime || null,
    hasOptimizedTimes: firstStop?.estimatedArrivalTime !== null && firstStop?.estimatedArrivalTime !== undefined
  };
};

/**
 * Calculate when crew should leave yard based on first arrival
 * @param {Date} selectedDate - The date for the route
 * @param {number} firstArrivalSeconds - First stop arrival time in seconds
 * @param {number} driveTimeMinutes - Drive time from yard to first stop
 * @returns {moment.Moment} Yard departure time
 */
export const calculateYardDepartureTime = (selectedDate, firstArrivalSeconds, driveTimeMinutes) => {
  return moment(selectedDate)
    .startOf('day')
    .add(firstArrivalSeconds, 'seconds')
    .subtract(driveTimeMinutes, 'minutes');
};

/**
 * Calculate arrival and departure times for a stop
 * @param {Object} stop - Stop object with time data
 * @param {Date} selectedDate - The date for the route
 * @returns {Object} Moment objects for arrival and departure
 */
export const calculateStopTimes = (stop, selectedDate) => {
  if (!stop || stop.estimatedArrivalTime == null || stop.estimatedDepartureTime == null) {
    return {
      arrival: null,
      departure: null,
      serviceDuration: 0
    };
  }

  return {
    arrival: moment(selectedDate).startOf('day').add(stop.estimatedArrivalTime, 'seconds'),
    departure: moment(selectedDate).startOf('day').add(stop.estimatedDepartureTime, 'seconds'),
    serviceDuration: stop.estimatedDepartureTime - stop.estimatedArrivalTime
  };
};

/**
 * Format a time range for display
 * @param {moment.Moment} startTime - Start time
 * @param {moment.Moment} endTime - End time
 * @returns {string} Formatted time range
 */
export const formatTimeRange = (startTime, endTime) => {
  if (!startTime || !endTime) return '';
  
  return `${startTime.format('h:mm A')} - ${endTime.format('h:mm A')}`;
};

/**
 * Calculate drive duration between two stops
 * @param {Object} prevStop - Previous stop
 * @param {Object} currentStop - Current stop
 * @returns {number} Drive duration in minutes
 */
export const calculateDriveDuration = (prevStop, currentStop) => {
  if (!prevStop || !currentStop) return 0;
  
  // If we have explicit drive duration, use it
  if (currentStop.estimatedDriveDuration) {
    return Math.round(currentStop.estimatedDriveDuration / 60);
  }
  
  // Otherwise calculate from arrival/departure times
  if (prevStop.estimatedDepartureTime != null && currentStop.estimatedArrivalTime != null) {
    const durationSeconds = currentStop.estimatedArrivalTime - prevStop.estimatedDepartureTime;
    return Math.round(durationSeconds / 60);
  }
  
  return 15; // Default fallback
};

/**
 * Get default workday bounds for timeline view
 * @param {Date} date - The date
 * @returns {Object} Start and end times for workday
 */
export const getDefaultWorkdayBounds = (date) => {
  const dayStart = moment(date).startOf('day');
  
  return {
    start: dayStart.clone().hour(5),     // 5 AM
    end: dayStart.clone().hour(19),      // 7 PM (extended from 6 PM to prevent stacking)
    minTime: dayStart.valueOf(),
    maxTime: dayStart.clone().endOf('day').valueOf()
  };
};

/**
 * Calculate average drive time per stop for fallback calculations
 * @param {Object} route - Route object
 * @returns {number} Average drive time in minutes
 */
export const calculateAverageDriveTime = (route) => {
  if (!route || !route.stops?.length) {
    return 15; // Default fallback
  }
  
  // Check if totalDriveTime is a valid number
  const totalDriveTime = route.totalDriveTime;
  if (typeof totalDriveTime !== 'number' || isNaN(totalDriveTime) || totalDriveTime <= 0) {
    return 15; // Default fallback
  }
  
  // Add 1 to account for return to yard
  const avgTime = Math.round(totalDriveTime / (route.stops.length + 1));
  
  // Ensure the result is a valid number between 5 and 60 minutes
  return Math.max(5, Math.min(60, avgTime));
};

/**
 * Convert seconds since day start to moment object
 * @param {Date} date - Base date
 * @param {number} seconds - Seconds since start of day
 * @returns {moment.Moment} Moment object
 */
export const secondsToMoment = (date, seconds) => {
  if (seconds == null || isNaN(seconds)) return null;
  
  return moment(date).startOf('day').add(seconds, 'seconds');
};

/**
 * Calculate total route duration including drive and service time
 * @param {Object} route - Route object
 * @returns {Object} Duration breakdown in minutes
 */
export const calculateRouteDuration = (route) => {
  const timeData = extractRouteTimeData(route);
  
  const totalMinutes = timeData.totalDriveTime + timeData.totalServiceTime;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  return {
    totalMinutes,
    hours,
    minutes,
    driveMinutes: timeData.totalDriveTime,
    serviceMinutes: timeData.totalServiceTime,
    displayText: `${hours}h ${minutes}m`
  };
};

/**
 * Check if a time falls within workday bounds
 * @param {moment.Moment} time - Time to check
 * @param {Object} bounds - Workday bounds from getDefaultWorkdayBounds
 * @returns {boolean} True if within bounds
 */
export const isWithinWorkday = (time, bounds) => {
  if (!time || !bounds) return false;
  
  return time.valueOf() >= bounds.start.valueOf() && 
         time.valueOf() <= bounds.end.valueOf();
};

/**
 * Calculate estimated finish time for a route
 * @param {Object} route - Route object
 * @param {Date} selectedDate - Route date
 * @returns {moment.Moment|null} Estimated finish time
 */
export const calculateRouteFinishTime = (route, selectedDate) => {
  if (!route || !selectedDate) {
    return null;
  }
  
  const timeData = extractRouteTimeData(route);
  
  if (timeData.lastDeparture != null) {
    // Add drive time back to yard
    const avgDriveTime = calculateAverageDriveTime(route);
    const lastDepartureTime = secondsToMoment(selectedDate, timeData.lastDeparture);
    
    if (lastDepartureTime && typeof avgDriveTime === 'number') {
      return lastDepartureTime.add(avgDriveTime, 'minutes');
    }
  }
  
  return null;
};

/**
 * Format seconds as hours and minutes
 * @param {number} seconds - Total seconds
 * @returns {string} Formatted time string
 */
export const formatDuration = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0m';
  
  const minutes = Math.round(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (hours > 0) {
    return `${hours}h ${remainingMinutes}m`;
  }
  
  return `${minutes}m`;
};